// Time-based "light-trail" (comet) reading guide for Guided mode.
//
// Drives per-token background fades via the Web Animations API — there is NO
// requestAnimationFrame loop (so it never competes with the smooth-follow
// scroller), NO React re-render of the text, and NO layout reflow (only
// background/text colour changes). When a word leaves the head it fades from the
// head colour to transparent over T ms (linear), so words further back are
// naturally dimmer because their fade started earlier → a continuous comet trail
// rather than discrete on/off words. A small word cap keeps the trail from
// painting half a paragraph at very high WPM.

export const TRAIL_MS = { short: 550, medium: 800, long: 1150 };
export const TRAIL_CAP = { short: 5, medium: 7, long: 10 };

// Clean marker yellow / warm amber per reader theme (no neon/glow).
//  base = the per-token trail tint that fades over time (soft ghost of read words)
//  zero = same hue at alpha 0 (fade target)
//  hot  = the gliding head hotspot colour (the moving bright point). Kept moderate
//         so normal text stays legible WITHOUT a per-word dark-ink flip (which read
//         as stepped) — the guide is the smooth motion, not a hard colour switch.
export const TRAIL_COLOR = {
    night: { base: "hsla(48, 100%, 60%, 0.38)", zero: "hsla(48, 100%, 60%, 0)", hot: "hsla(48, 100%, 60%, 0.66)" },
    day: { base: "hsla(45, 100%, 52%, 0.52)", zero: "hsla(45, 100%, 52%, 0)", hot: "hsla(45, 100%, 50%, 0.85)" },
    sepia: { base: "hsla(40, 92%, 50%, 0.46)", zero: "hsla(40, 92%, 50%, 0)", hot: "hsla(40, 92%, 48%, 0.82)" },
};

export function createTrail() {
    let queue = [];        // [{ el, anim }] oldest → newest, fading tail words
    let head = new Set();  // tokens currently at full head intensity

    const removeFromQueue = (el) => {
        const i = queue.findIndex((q) => q.el === el);
        if (i >= 0) { const [q] = queue.splice(i, 1); return q; }
        return null;
    };

    const enterHead = (el, headColor) => {
        const q = removeFromQueue(el);
        if (q) { try { q.anim.cancel(); } catch (e) {} }
        el.style.backgroundColor = headColor; // .rg-tok CSS transition eases it in softly
    };

    const leaveHead = (el, headColor, zero, T) => {
        let anim;
        try {
            anim = el.animate(
                [{ backgroundColor: headColor }, { backgroundColor: zero }],
                { duration: T, easing: "linear", fill: "forwards" }
            );
        } catch (e) {
            el.style.backgroundColor = "";
            return;
        }
        const entry = { el, anim };
        anim.onfinish = () => {
            el.style.backgroundColor = "";      // clear inline → category/base bg returns
            try { anim.cancel(); } catch (e) {}
            removeFromQueue(el);
        };
        queue.push(entry);
    };

    const enforceCap = (cap) => {
        while (queue.length > cap) {
            const { el, anim } = queue.shift();
            try { anim.cancel(); } catch (e) {}
            el.style.backgroundColor = "";       // oldest tail is already near-zero → invisible cut
        }
    };

    const clear = () => {
        for (const { el, anim } of queue) { try { anim.cancel(); } catch (e) {} el.style.backgroundColor = ""; }
        queue = [];
        for (const el of head) { el.style.backgroundColor = ""; }
        head = new Set();
    };

    // Move the head to `els`. Tokens leaving the head begin their timed fade;
    // `immediate` (jump/seek/reset) wipes the trail first so nothing streaks.
    const setHead = (els, { immediate, headColor, zero, T, cap }) => {
        if (immediate) clear();
        const next = new Set(els);
        for (const el of head) if (!next.has(el)) leaveHead(el, headColor, zero, T);
        for (const el of els) if (!head.has(el)) enterHead(el, headColor);
        head = next;
        enforceCap(cap);
    };

    return { setHead, clear };
}
